/**
 * Operator: one deployment (plan §18.2, §26).
 *
 * Resource identity, the latest provisioning run with its steps, a SAFE retry (legal only from
 * PROVISIONING_FAILED / VERIFYING_FAILED), and the recent audit trail for this tenant.
 *
 * Secret REFS are shown because they are pointers an operator needs to confirm wiring; the values
 * behind them require Secret Manager IAM and never come down this wire (API-CONTRACT §8).
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../../api/client.js";
import { Banner, Loading, formatDate } from "../../components/ui.js";
import { AdminError, AdminNav } from "./shared.js";
import type { AdminDeployment, AuditEvent, ProvisioningStep } from "../../api/types.js";

interface Detail {
  deployment: AdminDeployment & Record<string, unknown>;
  run: { id: string; status: string; attempt: number; trigger: string; failure_code: string | null } | null;
  steps: (ProvisioningStep & { step_key?: string; step_index?: number })[];
  audit: AuditEvent[];
}

const RETRYABLE = new Set(["PROVISIONING_FAILED", "VERIFYING_FAILED"]);

export default function AdminDeploymentDetail() {
  const { id } = useParams<{ id: string }>();
  const [detail, setDetail] = useState<Detail | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      setDetail((await api.adminDeployment(id)) as unknown as Detail);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function retry() {
    if (!id) return;
    setBusy(true);
    setNotice(null);
    try {
      const res = await api.adminRetry(id);
      setNotice(`Started run ${res.runId} (attempt ${res.attempt}).`);
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <Loading label="Loading deployment" />;
  if (error && !detail) {
    return (
      <>
        <AdminNav />
        <AdminError error={error} onRetry={() => void load()} />
      </>
    );
  }
  if (!detail) return null;

  const d = detail.deployment;
  const canRetry = RETRYABLE.has(d.status);

  return (
    <>
      <div className="page-head">
        <p className="eyebrow">
          <Link to="/admin">Fleet</Link> / deployment
        </p>
        <h1 className="mono" style={{ fontSize: "1.4rem", textTransform: "none" }}>
          {d.ownerUid}
        </h1>
      </div>
      <AdminNav />

      {notice ? <Banner kind="info">{notice}</Banner> : null}
      {error ? <AdminError error={error} /> : null}

      <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))" }}>
        <div className="card">
          <h3>Identity</h3>
          <table className="table" style={{ marginTop: 12 }}>
            <tbody>
              {(
                [
                  ["State", d.status],
                  ["Account", d.accountId],
                  ["Plan", d.planCode],
                  ["Region", d.regionCode],
                  ["Vault repo", d.githubRepoFullName],
                  ["Bucket", d.r2BucketName],
                  ["DB schema", d.constructDbSchema],
                  ["Service", d.cloudRunServiceName],
                  ["Service URL", d.cloudRunServiceUrl],
                  ["Release", d.platformRelease],
                  ["Last verified", formatDate(d.lastVerifiedAt)],
                  ["Created", formatDate(d.createdAt)],
                ] as [string, string | null | undefined][]
              ).map(([k, v]) => (
                <tr key={k}>
                  <th scope="row" style={{ width: 150 }}>
                    {k}
                  </th>
                  <td className="mono small" style={{ wordBreak: "break-all" }}>
                    {v ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="card">
          <h3>Secret references</h3>
          <p className="muted small" style={{ marginTop: 6 }}>
            Pointers only. Reading the values behind them requires Secret Manager IAM.
          </p>
          <table className="table" style={{ marginTop: 12 }}>
            <tbody>
              {Object.entries(d.secretRefs ?? {}).map(([k, v]) => (
                <tr key={k}>
                  <th scope="row" style={{ width: 120 }}>
                    {k}
                  </th>
                  <td className="mono small" style={{ wordBreak: "break-all" }}>
                    {v ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ---- provisioning run ------------------------------------------------ */}
      <h2 style={{ marginTop: 40, marginBottom: 16 }}>Latest provisioning run</h2>
      {!detail.run ? (
        <div className="empty">No provisioning run recorded.</div>
      ) : (
        <div className="card">
          <div className="card__head">
            <div>
              <h3 className="mono" style={{ textTransform: "none" }}>
                {detail.run.id}
              </h3>
              <p className="muted small" style={{ marginTop: 6 }}>
                {detail.run.trigger} · attempt {detail.run.attempt} · {detail.run.status}
                {detail.run.failure_code ? ` · ${detail.run.failure_code}` : ""}
              </p>
            </div>
            <button type="button" className="btn btn--ghost btn--sm" disabled={!canRetry || busy} onClick={() => void retry()}>
              {busy ? "Starting" : "Retry"}
            </button>
          </div>
          {!canRetry ? (
            <p className="muted small" style={{ marginTop: 10 }}>
              Retry is only legal from PROVISIONING_FAILED or VERIFYING_FAILED.
            </p>
          ) : null}
          <ul className="steps" style={{ marginTop: 16 }}>
            {detail.steps.map((s, i) => {
              const key = s.step_key ?? s.key ?? String(i);
              return (
                <li key={key} data-state={s.state}>
                  <span className="steps__dot" data-state={s.state} aria-hidden="true" />
                  <span className="steps__label mono small">{key}</span>
                  <span className="mono small muted">{s.state}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* ---- audit ----------------------------------------------------------- */}
      <h2 style={{ marginTop: 40, marginBottom: 16 }}>Recent audit</h2>
      <div className="card table-scroll" style={{ padding: 0 }}>
        <table className="table">
          <thead>
            <tr>
              <th scope="col">When</th>
              <th scope="col">Actor</th>
              <th scope="col">Action</th>
              <th scope="col">Metadata</th>
            </tr>
          </thead>
          <tbody>
            {(detail.audit ?? []).map((e) => (
              <tr key={e.id}>
                <td className="mono small">{formatDate(e.created_at)}</td>
                <td className="muted small">{e.actor}</td>
                <td className="mono small">{e.action}</td>
                <td className="mono small muted" style={{ maxWidth: 380, wordBreak: "break-all" }}>
                  {JSON.stringify(e.metadata)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
